// src/components/SystemResetModal.tsx - NEW: Modal for system reset with safety checks
import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAppContext } from '../hooks/useAppContext';
import './shared.css';

interface SystemResetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CONFIRMATION_PHRASE = 'DELETE ALL APPLICATION DATA';

const SystemResetModal: React.FC<SystemResetModalProps> = ({
  isOpen,
  onClose
}) => {
  const { systemReset } = useApi();
  const { showToast } = useAppContext();
  
  const [confirmationText, setConfirmationText] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
      setIsResetting(false);
    }
  }, [isOpen]);

  // Handle system reset
  const handleReset = useCallback(async () => {
    if (confirmationText !== CONFIRMATION_PHRASE) {
      showToast({
        type: 'error',
        title: 'Invalid Confirmation',
        message: 'Please type the exact confirmation phrase'
      });
      return;
    }

    setIsResetting(true);
    try {
      const result = await systemReset(confirmationText) as { success: boolean; deleted_indexes?: string[]; message?: string };
      
      if (result && result.success) {
        showToast({
          type: 'success',
          title: 'System Reset Complete',
          message: `Successfully deleted ${result.deleted_indexes?.length || 0} indexes. ${result.message}`,
          duration: 10000
        });
        
        onClose();
        
        // Show additional details if available
        if (result.deleted_indexes && result.deleted_indexes.length > 0) {
          setTimeout(() => {
            showToast({
              type: 'info',
              title: 'Deleted Indexes',
              message: `Removed: ${result.deleted_indexes?.join(', ') || 'None'}`,
              duration: 8000
            });
          }, 1000);
        }
      } else {
        throw new Error('System reset failed - no success confirmation received');
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Reset Failed',
        message: error instanceof Error ? error.message : 'System reset failed'
      });
    } finally {
      setIsResetting(false);
    }
  }, [confirmationText, systemReset, showToast, onClose]);

  // Handle escape key (only when not resetting)
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isResetting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isResetting, onClose]);

  if (!isOpen) return null;

  const isConfirmationValid = confirmationText === CONFIRMATION_PHRASE;

  return (
    <div className="modal-overlay" onClick={isResetting ? undefined : onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={20} />
            System Reset
          </h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={isResetting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="warning-box">
            <AlertTriangle size={16} />
            <strong>DANGER: This action cannot be undone!</strong>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#dc2626' }}>This will permanently delete:</h3>
            <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151', lineHeight: '1.6' }}>
              <li><strong>All contact data</strong> (saved contacts from people index)</li>
              <li><strong>All user lists</strong> (contact lists and saved searches)</li>
              <li><strong>All background jobs</strong> (export jobs, processing history)</li>
              <li><strong>All application caches</strong> (metadata, suggestions)</li>
            </ul>
          </div>

          <div style={{ marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>Protected Data (will NOT be deleted):</h4>
            <ul style={{ margin: '0', paddingLeft: '20px', color: '#64748b', fontSize: '14px' }}>
              <li>Primary people database</li>
              <li>User accounts and API keys</li>
              <li>System configuration</li>
            </ul>
          </div>

          <div className="form-group">
            <label htmlFor="confirmation-text" className="form-label" style={{ color: '#dc2626' }}>
              Type "<strong>{CONFIRMATION_PHRASE}</strong>" to confirm:
            </label>
            <input
              id="confirmation-text"
              type="text"
              value={confirmationText}
              onChange={e => setConfirmationText(e.target.value)}
              placeholder="Type the confirmation phrase exactly..."
              disabled={isResetting}
              className={`form-input ${confirmationText && !isConfirmationValid ? 'error' : ''}`}
              style={{ fontFamily: 'monospace' }}
            />
            {confirmationText && !isConfirmationValid && (
              <div className="error-message">
                <AlertTriangle size={12} />
                Confirmation phrase does not match exactly
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={isResetting}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger"
            onClick={handleReset}
            disabled={!isConfirmationValid || isResetting}
          >
            {isResetting ? (
              <>
                <Loader2 size={16} className="loading-spinner" />
                Resetting System...
              </>
            ) : (
              <>
                <Trash2 size={16} />
                Confirm System Reset
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemResetModal;